import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useLang } from '@/context/LanguageContext';
import { LANGS } from '@/i18n/translations';
import { formatFullDate } from '@/data/catalog';
import { X, Clock, MapPin, Globe, Users, Calendar, Sparkles, FileText, AlertTriangle } from 'lucide-react';

// Stable animation configs (defined once, not recreated on every render)
const OVERLAY_FADE = { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } };
const PANEL_MOTION = {
  initial: { opacity: 0, y: 24, scale: 0.98 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: 24, scale: 0.98 },
  transition: { duration: 0.28, ease: 'easeOut' },
};


export const ShowModal = ({ show, onClose, showDates = true }) => {
  const { t, lang } = useLang();
  const [tab, setTab] = useState(lang);

  useEffect(() => { setTab(lang); }, [lang, show]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [onClose]);

  if (!show) return null;

  const isCancelled = !!show.cancelat;

  const meta = [
    { icon: Sparkles, label: t.modal.company, value: show.compania },
    { icon: Globe, label: t.modal.country, value: show.pais[lang] },
    // Sin fecha propia ni rango escrito, la fila no se pinta: antes caía en la
    // fecha de la edición 2026, que se retiró al acabar el festival. Y con
    // showDates={false} no se pinta ninguna de las dos, ni fecha ni hora.
    ...(showDates && (show.dia || show.rangLabel)
      ? [{
          icon: Calendar,
          label: show.dia ? t.modal.date : t.modal.dates,
          value: show.dia ? formatFullDate(show.dia, t.months) : show.rangLabel[lang],
        }]
      : []),
    ...(showDates && show.hora ? [{ icon: Clock, label: t.modal.time, value: show.hora }] : []),
    { icon: MapPin, label: t.modal.venue, value: show.ubicacio },
    { icon: Users, label: t.modal.audience, value: show.publico[lang] },
  ];

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[100] flex items-stretch sm:items-center justify-center p-0 sm:p-6"
        {...OVERLAY_FADE}
        data-testid="show-modal"
      >
        <div className="absolute inset-0 bg-[var(--cea-overlay)] backdrop-blur-sm" onClick={onClose} />
        <motion.div
          {...PANEL_MOTION}
          className="relative z-10 w-full sm:max-w-4xl bg-[#0b241b] sm:rounded-2xl border border-[var(--cea-border-strong)] overflow-hidden flex flex-col max-h-screen sm:max-h-[90vh]"
        >
          <button
            data-testid="show-modal-close"
            onClick={onClose}
            className="absolute top-4 right-4 z-20 grid h-10 w-10 place-items-center rounded-full bg-[#040f0c]/80 backdrop-blur border border-[var(--cea-border)] text-white hover:text-[var(--cea-accent)] transition-colors duration-200"
            aria-label={t.modal.close}
          >
            <X size={20} />
          </button>

          <div className="grid grid-cols-1 md:grid-cols-5 overflow-y-auto">
            {/* image */}
            <div className="md:col-span-2 relative">
              <img src={show.imagen} alt={show.titol} className={`h-56 md:h-full w-full object-cover ${isCancelled ? 'grayscale opacity-50' : ''}`} />
              <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-[#0b241b] via-transparent to-transparent" />
              {isCancelled ? (
                <span className="absolute top-4 left-4 rounded-full bg-[var(--cea-lamp-red)] px-3 py-1 text-xs font-bold uppercase tracking-wide text-white">
                  {t.avis.badge}
                </span>
              ) : (
                <span className="absolute top-4 left-4 rounded-full bg-[var(--cea-accent)] px-3 py-1 text-xs font-bold text-[#040f0c]">
                  {show.typeBadge ? show.typeBadge[lang] : (show.tipus === 'installacio' ? t.agenda.installation : t.agenda.free)}
                </span>
              )}
            </div>

            {/* content */}
            <div className="md:col-span-3 p-6 sm:p-8">
              <p className="text-sm font-bold tracking-wide text-[var(--cea-accent)]">{show.disciplina[lang]}</p>
              <h2 className={`mt-1 font-display font-medium text-3xl sm:text-4xl leading-[0.98] tracking-[-0.02em] uppercase ${
                isCancelled ? 'text-white/65 line-through decoration-[var(--cea-lamp-red)] decoration-2' : ''
              }`}>{show.titol}</h2>
              <p className="mt-2 text-base font-semibold text-white/80">{show.compania}</p>

              {/* Comunicado de cancelación — texto tal cual lo firma la dirección del festival */}
              {isCancelled && (
                <div
                  data-testid="show-modal-cancelled"
                  className="mt-5 rounded-xl border border-[var(--cea-lamp-red)]/45 bg-[var(--cea-lamp-red)]/10 p-4"
                >
                  <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-[var(--cea-lamp-red)]">
                    <AlertTriangle size={15} /> {t.avis.cancelled}
                  </p>
                  {show.avis && (
                    <p className="mt-2 text-sm leading-relaxed text-white/85">{show.avis[lang]}</p>
                  )}
                </div>
              )}

              <div className="mt-5 grid grid-cols-2 gap-x-4 gap-y-3">
                {meta.map((m) => (
                  <div key={m.label} className="flex items-start gap-2">
                    <m.icon size={15} className="mt-0.5 text-[var(--cea-accent)] flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-[11px] uppercase tracking-wide text-white/45">{m.label}</p>
                      <p className="text-sm text-white/90">{m.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* synopsis with language tabs */}
              <div className="mt-6">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <p className="text-[11px] uppercase tracking-wide text-white/45">{t.modal.synopsis}</p>
                  <div className="inline-flex rounded-full border border-[var(--cea-border)] p-0.5">
                    {LANGS.map((l) => (
                      <button
                        key={l.code}
                        data-testid={`modal-lang-${l.code}`}
                        onClick={() => setTab(l.code)}
                        className={`px-2.5 py-0.5 text-[11px] font-bold rounded-full transition-colors duration-200 ${
                          tab === l.code ? 'bg-[var(--cea-accent)] text-[#040f0c]' : 'text-white/55 hover:text-white'
                        }`}
                      >{l.label}</button>
                    ))}
                  </div>
                </div>
                <p className="text-sm sm:text-[15px] text-white/80 leading-relaxed">{show.sinopsis[tab]}</p>
              </div>

              {/* Photo-exhibition PDFs (one per language) — QR-ready direct links */}
              {show.pdfs && (
                <div className="mt-6" data-testid="exhibition-pdfs">
                  <p className="text-[11px] uppercase tracking-wide text-white/45 mb-2">
                    {{ ca: "Llegeix l'exposició fotogràfica", es: 'Lee la exposición fotográfica', fr: "Lire l'exposition photographique" }[lang]}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {[{ c: 'es', l: 'Español' }, { c: 'en', l: 'English' }, { c: 'fr', l: 'Français' }].map((o) => (
                      <a
                        key={o.c}
                        href={show.pdfs[o.c]}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`exhibition-pdf-${o.c}`}
                        className="inline-flex items-center gap-2 rounded-full border border-[var(--cea-border-strong)] bg-[#12382c] px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-[var(--cea-accent)] hover:text-[#040f0c] transition-colors duration-200"
                      >
                        <FileText size={14} /> {o.l}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
